import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "@/assets/logo-liberotratto.png";
import { formatDecimal } from "./formatting";
import { companyProfile, companyHeaderLines } from "./companyProfile";
import { trackEvent, EVENTS } from "./analytics";

/* Unità di vendita di un articolo: mq se ha i mq/scatola, ml se ha i ml/confezione
   (per i battiscopa i ml hanno la precedenza), pezzi se non è venduto a scatola. */
function boxUnitOf(item, isBattiscopa, isArea) {
  const sqm = Number(item?.sqmPerBox || 0);
  const ml = Number(item?.mlPerBox || 0);
  if (isBattiscopa && ml > 0) return { perBox: ml, unit: "mL" };
  if (sqm > 0) return { perBox: sqm, unit: "mQ" };
  if (ml > 0) return { perBox: ml, unit: "mL" };
  // articolo venduto a mq/ml ma senza il dato per scatola: si assume 1 per
  // scatola, come fanno il form e l'API (COALESCE(sqmPerBox, 1))
  if (isBattiscopa) return { perBox: 1, unit: "mL" };
  if (isArea) return { perBox: 1, unit: "mQ" };
  return { perBox: 0, unit: "pz" };
}

/* Quantità da stampare per una riga venduta a scatola/confezione.
   Priorità ai mq/ml in corso di modifica nel form (mqOverrides), poi a quelli
   persistiti sulla riga (`mqQuantity`); in mancanza di entrambi si ricava dal
   numero di scatole (righe create prima del campo dedicato). */
function areaQuantity(itemId, boxes, perBox, mqOverrides, persistedMq) {
  const typed = Number(mqOverrides?.[itemId]);
  if (typed > 0) return Number(typed.toFixed(2));
  const saved = Number(persistedMq);
  if (saved > 0) return Number(saved.toFixed(2));
  return Number((Number(boxes || 0) * perBox).toFixed(2));
}

function buildQuoteTable(quote, pageWidth, marginX, mqOverrides = {}) {
  const head = [
    [
      "Cod.",
      "Descrizione",
      "Q.tà",
      "U.m.",
      "Imponibile",
      "Aliquota",
      "Imposta",
      "Totale",
    ],
  ];

  const lower = (v) => String(v || "").toLowerCase();

  const rows = [];
  let hasLineDiscount = false; // true se almeno una riga usa il prezzo scontato
  const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

  const AREA_CATS_PDF = [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ];

  (quote.items || []).forEach((it) => {
    const name = it.itemName || it.subBrand || it.brand || "-";
    const isArea = AREA_CATS_PDF.includes(lower(it.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    // Articoli venduti a scatola/confezione: la quantità si esprime in mq/ml,
    // non in scatole (vale per tutto il gruppo area, non solo le piastrelle).
    const { perBox, unit } = boxUnitOf(it, isBattiscopa, isArea);
    const isBoxBased = perBox > 0;

    const rawQty =
      it?.QuoteItem?.quantity ??
      (isBoxBased ? it?.boxQuantity : it?.pieces) ??
      0;
    const baseQty = Number(rawQty);
    const qty = isBoxBased
      ? areaQuantity(
          it.id,
          baseQty,
          perBox,
          mqOverrides,
          it?.QuoteItem?.mqQuantity ?? it?.mqQuantity
        )
      : baseQty;

    // Prezzo netto applicato (nuova semantica IVA esclusa): le righe storiche
    // migrate espongono il netto in discountedPrice; altrimenti è `price`.
    const dpThrough = it?.QuoteItem?.discountedPrice ?? it?.discountedPrice;
    const priceThrough = Number(it?.QuoteItem?.price ?? it.price ?? 0);
    const appliedUnitNet =
      dpThrough !== null && dpThrough !== undefined
        ? Number(dpThrough)
        : priceThrough;
    if (dpThrough !== null && dpThrough !== undefined) hasLineDiscount = true;
    // IVA della RIGA preventivo (through), poi fallback su articolo, poi 22.
    // Sempre ?? perché 0 (IVA 0%) è un valore valido.
    const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    // Importi di RIGA (quantità inclusa): imponibile, imposta, totale lordo.
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const dim =
      it.formatLabel ||
      (it.widthCm && it.heightCm ? `${it.widthCm}×${it.heightCm}` : null);
    const normStr = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr = it.thicknessMm ? `${parseFloat(it.thicknessMm)}mm` : null;
    const specParts = isArea
      ? [
          dim && !normStr(name).includes(normStr(dim)) ? dim : null,
          it.surface,
          thickStr && !name.toLowerCase().includes(thickStr) ? thickStr : null,
        ].filter(Boolean)
      : [];
    const desc = specParts.length
      ? `${name} ${specParts.join(" ")}`.trim()
      : name;

    rows.push({
      group: String(it.category || "Generale")
        .trim()
        .toUpperCase(),
      code: it.supplierCode || it.itemCode || it.code || "-",
      desc,
      qty,
      unit,
      taxable: taxableAmount,
      iva,
      tax: taxAmount,
      grossTotal,
    });
  });

  (quote.quote_items || []).forEach((qi) => {
    const id = qi?.item?.id;
    if (id && itemMap.has(id)) return;

    const it2 = qi?.item || {};
    const name = it2.itemName || "-";

    const isArea2 = AREA_CATS_PDF.includes(lower(it2.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    // qi.quantity è in scatole: va convertita in mq/ml come nel blocco sopra,
    // altrimenti l'importo di riga (prezzo al mq × scatole) risulta errato.
    const { perBox, unit } = boxUnitOf(it2, isBattiscopa, isArea2);
    const qty =
      perBox > 0
        ? areaQuantity(it2.id, qi.quantity, perBox, mqOverrides, qi.mqQuantity)
        : Number(qi.quantity || 0);

    // Prezzo netto applicato (nuova semantica IVA esclusa).
    const dpQi = qi.discountedPrice;
    const appliedUnitNet =
      dpQi !== null && dpQi !== undefined
        ? Number(dpQi)
        : Number(qi.price || 0);
    if (dpQi !== null && dpQi !== undefined) hasLineDiscount = true;
    const iva = qi?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const group = String(it2.category || "Generale")
      .trim()
      .toUpperCase();

    const dim2 =
      it2.formatLabel ||
      (it2.widthCm && it2.heightCm ? `${it2.widthCm}×${it2.heightCm}` : null);
    const normStr2 = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr2 = it2.thicknessMm
      ? `${parseFloat(it2.thicknessMm)}mm`
      : null;
    const specParts2 = isArea2
      ? [
          dim2 && !normStr2(name).includes(normStr2(dim2)) ? dim2 : null,
          it2.surface,
          thickStr2 && !name.toLowerCase().includes(thickStr2)
            ? thickStr2
            : null,
        ].filter(Boolean)
      : [];
    const desc2 = specParts2.length
      ? `${name} ${specParts2.join(" ")}`.trim()
      : name;

    rows.push({
      group,
      code: it2.supplierCode || it2.itemCode || it2.code || "-",
      desc: desc2,
      qty,
      unit,
      taxable: taxableAmount,
      iva,
      tax: taxAmount,
      grossTotal,
    });
  });

  const order = [];
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.group]) {
      groups[r.group] = [];
      order.push(r.group);
    }
    groups[r.group].push(r);
  });

  const body = [];
  order.forEach((g) => {
    body.push([
      {
        content: g,
        colSpan: 8,
        styles: {
          fillColor: [235, 235, 235],
          fontStyle: "bold",
          halign: "left",
        },
      },
    ]);
    groups[g].forEach((r) => {
      body.push([
        r.code,
        r.desc,
        String(r.qty).replace(".", ","),
        r.unit,
        `€ ${formatDecimal(r.taxable)}`,
        `${r.iva}%`,
        `€ ${formatDecimal(r.tax)}`,
        `€ ${formatDecimal(r.grossTotal)}`,
      ]);
    });
  });

  const usable = pageWidth - 2 * marginX;
  const w = {
    cod: 0.09,
    qty: 0.08,
    um: 0.07,
    taxable: 0.15,
    aliquota: 0.07,
    imposta: 0.12,
    totale: 0.15,
  };
  const cw = {
    0: Math.floor(usable * w.cod),
    2: Math.floor(usable * w.qty),
    3: Math.floor(usable * w.um),
    4: Math.floor(usable * w.taxable),
    5: Math.floor(usable * w.aliquota),
    6: Math.floor(usable * w.imposta),
    7: Math.floor(usable * w.totale),
  };

  const rowsTaxableTotal = Number(
    rows.reduce((s, r) => s + Number(r.taxable || 0), 0).toFixed(2)
  );
  const rowsTaxTotal = Number(
    rows.reduce((s, r) => s + Number(r.tax || 0), 0).toFixed(2)
  );
  const rowsItemsTotal = Number(
    rows.reduce((s, r) => s + Number(r.grossTotal || 0), 0).toFixed(2)
  );

  return {
    head,
    body,
    hasLineDiscount,
    rowsTaxableTotal,
    rowsTaxTotal,
    rowsItemsTotal,
    tableWidth: usable,
    margin: { left: marginX, right: marginX },
    columnStyles: {
      0: { cellWidth: cw[0], halign: "left" },
      1: { cellWidth: "auto", minCellWidth: 30, halign: "left" },
      2: { cellWidth: cw[2], halign: "right" },
      3: { cellWidth: cw[3], halign: "center" },
      4: { cellWidth: cw[4], halign: "right" },
      5: { cellWidth: cw[5], halign: "center" },
      6: { cellWidth: cw[6], halign: "right" },
      7: { cellWidth: cw[7], halign: "right" },
    },
  };
}

/**
 * @param {object} params
 * @param {object} params.quote         - dati preventivo/ordine completi
 * @param {object} params.toast         - istanza vue-toastification ($toast)
 * @param {Function} params.uploadQuoteDocument - fn(payload) che esegue il dispatch Vuex
 * @param {Function} [params.onComplete]        - callback dopo upload riuscito
 */
export async function generateQuotePdf({
  quote,
  toast,
  uploadQuoteDocument,
  onComplete,
  isOrder = false,
  filenameOverride = null,
  mqOverrides = {},
}) {
  try {
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    let y = marginY;

    if (logoPath) {
      try {
        doc.addImage(logoPath, "PNG", marginX - 5, y, 60, 24);
        // eslint-disable-next-line no-empty
      } catch {}
    }
    doc.setFontSize(9).setFont("helvetica", "normal");
    companyHeaderLines().forEach((text, i) => {
      const x = pageWidth - marginX - doc.getTextWidth(text);
      doc.text(text, x, y + 5 + i * 4);
    });
    y += 48;

    const printLabelValue = (
      label,
      value,
      xPos,
      yPos,
      maxWidth = null,
      link = null
    ) => {
      const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
      doc.setFont("helvetica", "bold");
      doc.text(`${labelCap}:`, xPos, yPos);
      const off = doc.getTextWidth(`${labelCap}: `) + 0.8;
      doc.setFont("helvetica", "normal");
      const val = value || "N/D";
      let display = val;
      if (maxWidth) {
        while (
          doc.getTextWidth(display) > maxWidth - off &&
          display.length > 3
        ) {
          display = display.slice(0, -4) + "…";
        }
      }
      if (link) {
        doc.setTextColor(0, 0, 255);
        doc.textWithLink(display, xPos + off, yPos, { url: link });
        doc.setTextColor(0);
      } else {
        doc.text(display, xPos + off, yPos);
      }
      return yPos + 4;
    };

    const contactValue = quote?.Client?.pec || quote?.Client?.email || "";
    const contactLabel = quote?.Client?.pec ? "pec" : "email";
    const lh = (() => {
      const raw = quote?.Client?.legalHeadquarters;
      if (!raw) return {};
      if (typeof raw === "object") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    })();
    const rightX = pageWidth / 2 - 10;
    const colW = pageWidth - rightX - marginX;
    const leftColW = rightX - marginX - 10;
    const formattedDate = new Date(quote.date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Ultimo pagamento usato per generare il PDF. Calcolato qui (prima del
    // blocco "Dettaglio ordine") perché serve sia per la data documento sia,
    // più in basso, per la riga di riepilogo e i dettagli pagamento.
    const lastPayment = [...(quote.payments || [])].sort((a, b) => {
      const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
      const dbb = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
      if (da !== dbb) return da - dbb;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    })[(quote.payments || []).length - 1];

    const formattedDocDate = lastPayment
      ? new Date(
          lastPayment.paidAt ?? lastPayment.createdAt ?? Date.now()
        ).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;

    // LEFT: Dettaglio cliente
    let leftX = marginX;
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text("Dettaglio cliente", leftX, y);
    let yLeft = y + 8;
    doc.setFontSize(9);
    yLeft = printLabelValue("cliente", quote?.Client?.clientName, leftX, yLeft);
    yLeft = printLabelValue(
      "telefono",
      quote?.Client?.phoneNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(contactLabel, contactValue, leftX, yLeft);
    yLeft = printLabelValue(
      "partita iva",
      quote?.Client?.vatNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "codice fiscale",
      quote?.Client?.taxCode,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "azienda",
      quote?.Client?.companyName,
      leftX,
      yLeft
    );
    yLeft = printLabelValue("sede legale", lh.address, leftX, yLeft, leftColW);

    // RIGHT: Dettaglio preventivo
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text(isOrder ? "Dettaglio ordine" : "Dettaglio preventivo", rightX, y);
    let yRight = y + 8;
    doc.setFontSize(9);
    if (lastPayment) {
      yRight = printLabelValue("data ordine", formattedDate, rightX, yRight);
      yRight = printLabelValue(
        "data documento",
        formattedDocDate,
        rightX,
        yRight
      );
    } else {
      yRight = printLabelValue("data", formattedDate, rightX, yRight);
    }
    // Modalità di evasione: in "ritiro" non c'è indirizzo di consegna.
    const isPickup = quote?.deliveryType === "ritiro";
    if (isPickup) {
      yRight = printLabelValue("modalità", "Ritiro", rightX, yRight, colW);
    } else {
      yRight = printLabelValue(
        "indirizzo di consegna",
        quote?.location?.address,
        rightX,
        yRight,
        colW,
        quote?.location?.link || null
      );
    }

    y = Math.max(yLeft, yRight) + 4;
    doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
    y += 6;

    const tbl = buildQuoteTable(quote, pageWidth, marginX, mqOverrides);
    autoTable(doc, {
      startY: y,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 1.5 },
      headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
      head: tbl.head,
      body: tbl.body,
      columnStyles: tbl.columnStyles,
      tableWidth: tbl.tableWidth,
      margin: tbl.margin,
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    const PACKAGING_COST = 30;
    const hasSurcharge = Number(quote.surcharge || 0) > 0;
    // Se almeno una riga ha un prezzo scontato, il totale ordine deriva dalla
    // somma delle righe (già scontate) per coerenza con la tabella; altrimenti
    // resta il totale calcolato lato server (comportamento invariato).
    const serverItemsTotal = hasSurcharge
      ? Number(quote.realTotalPrice || quote.totalPrice || 0)
      : Number(quote.totalPrice || 0);
    const itemsTotal =
      tbl.hasLineDiscount && tbl.rowsItemsTotal > 0
        ? tbl.rowsItemsTotal
        : serverItemsTotal;
    const base = itemsTotal + PACKAGING_COST;
    const percent = Number(quote.discount_percent || 0);
    const discountFromCol = Number(quote.discount || 0);
    const discountCalc = Number(((base * percent) / 100).toFixed(2));
    const discount = discountFromCol > 0 ? discountFromCol : discountCalc;
    const afterDiscount = Math.max(0, Number((base - discount).toFixed(2)));
    const deposit = Number(quote.deposit || 0);
    const toPay = Math.max(0, Number((afterDiscount - deposit).toFixed(2)));

    // Label + importo della riga pagamento basati sull'ultimo pagamento
    // registrato. "Totale pagato" mostra invece il cumulativo (deposit).
    const PAYMENT_TYPE_LABELS = {
      acconto: "Acconto",
      saldo_parziale: "Saldo parziale",
      saldo_finale: "Saldo finale",
    };
    const depositLabel = PAYMENT_TYPE_LABELS[lastPayment?.type] || "Acconto";

    const minVisualRoom = 55;
    if (pageHeight - y < minVisualRoom) {
      doc.addPage();
      y = marginY;
    }

    const headPadTop = 10;
    const lineH = 6;

    const leftTitle = "Riepilogo";
    // Formattazione display in euro in stile italiano (migliaia con punto,
    // decimali con virgola)
    const eur = (n) => formatDecimal(n);
    const leftLines = [
      `Totale imponibile: € ${eur(tbl.rowsTaxableTotal)}`,
      `Totale IVA: € ${eur(tbl.rowsTaxTotal)}`,
      `Totale ordine: € ${eur(itemsTotal)}`,
      `Spese imballo: € ${eur(PACKAGING_COST)}`,
    ];
    if (discount > 0) {
      const pctLabel = percent ? ` (${percent}%)` : "";
      leftLines.push(`Sconto${pctLabel}: € ${eur(discount)}`);
    }
    // Righe pagamenti: elenco completo dello storico (acconti + saldi), non
    // solo l'ultimo, così ogni versamento resta tracciato nel riepilogo.
    const paymentsSorted = [...(quote.payments || [])].sort((a, b) => {
      const da = new Date(a.paidAt ?? a.createdAt ?? 0).getTime();
      const db = new Date(b.paidAt ?? b.createdAt ?? 0).getTime();
      return da - db;
    });
    let anyPaymentLine = false;
    paymentsSorted.forEach((p) => {
      const amt = Number(p.amount || 0);
      if (amt <= 0) return;
      const label = PAYMENT_TYPE_LABELS[p.type] || "Acconto";
      leftLines.push(`${label}: € ${eur(amt)}`);
      anyPaymentLine = true;
    });
    // Fallback: nessun dettaglio pagamenti ma esiste un versato cumulativo
    if (!anyPaymentLine && deposit > 0) {
      leftLines.push(`${depositLabel}: € ${eur(deposit)}`);
    }
    // Totale cumulativo pagato
    if (deposit > 0) {
      leftLines.push(`Totale pagato: € ${eur(deposit)}`);
    }
    if (discount > 0 || deposit > 0) {
      leftLines.push({
        text: `Totale da pagare: € ${eur(toPay)}`,
        bold: true,
      });
    } else {
      leftLines.push({ text: `Totale: € ${eur(base)}`, bold: true });
    }

    const PAYMENT_METHOD_LABELS = {
      contanti: "Contanti",
      bonifico: "Bonifico",
      "carta di credito": "Carta di credito",
    };
    const centerTitle = "Dettagli pagamento";
    const centerLines = [
      `IBAN: ${companyProfile.iban}`,
      `Intestato a: ${companyProfile.name}`,
    ];
    // Metodo pagamento mostrato solo se presente sull'ultimo pagamento.
    if (lastPayment?.method) {
      const methodKey = String(lastPayment.method || "").toLowerCase();
      const methodLabel =
        PAYMENT_METHOD_LABELS[methodKey] || lastPayment.method;
      centerLines.unshift(`Metodo doc. corrente: ${methodLabel}`);
    }

    const rightTitle = "Firma";
    const sigLineYOffset = 12;

    const leftHeight = headPadTop + 2 + 7 + leftLines.length * lineH + 8;
    const centerHeight = headPadTop + 2 + 7 + centerLines.length * 8 + 8;
    const rightHeight = headPadTop + 2 + 7 + sigLineYOffset + 8;
    const minBlockH = 40;
    const blockH = Math.max(minBlockH, leftHeight, centerHeight, rightHeight);

    doc.setFillColor(240, 240, 240);
    doc.rect(marginX, y, pageWidth - 2 * marginX, blockH, "F");

    const colW3 = (pageWidth - 2 * marginX) / 3;
    const colPad = 4; // padding interno colonna (mm)
    const col1X = marginX + colPad; // bordo sinistro colonna 1 (Riepilogo)
    const col2X = marginX + colW3 + colPad; // bordo sinistro colonna 2 (Dettagli pagamento)
    const col3X = marginX + colW3 * 2.5; // Firma: invariata (centrata)

    // Colonna 1 — Riepilogo (allineata a sinistra)
    let fyLeft = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(leftTitle, col1X, fyLeft);
    fyLeft += 7;
    doc.setFont("helvetica", "normal").setFontSize(10);
    leftLines.forEach((ln) => {
      if (typeof ln === "string") {
        doc.text(ln, col1X, fyLeft);
      } else if (ln && ln.bold) {
        doc.setFont("helvetica", "bold");
        doc.text(ln.text, col1X, fyLeft);
        doc.setFont("helvetica", "normal");
      }
      fyLeft += lineH;
    });

    // Colonna 2 — Dettagli pagamento (allineata a sinistra)
    let fyCenter = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(centerTitle, col2X, fyCenter);
    fyCenter += 7;
    doc.setFont("helvetica", "normal").setFontSize(10);
    centerLines.forEach((ln, i) => {
      doc.text(ln, col2X, fyCenter + i * 8);
    });

    // Colonna 3 — Firma (centrata, linea firma sotto il titolo)
    const sigWidth = colW3 * 0.6;
    const sigStart = col3X - sigWidth / 2;
    const sigEnd = col3X + sigWidth / 2;
    let fyRight = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(rightTitle, col3X, fyRight, { align: "center" });
    fyRight += sigLineYOffset;
    doc.setDrawColor(0);
    doc.line(sigStart, fyRight, sigEnd, fyRight);

    y += blockH + 5;

    // ---- NOTE DEL PREVENTIVO ----
    if (quote.technical_queries) {
      const noteText = String(quote.technical_queries).trim();
      if (noteText) {
        doc.setFont("helvetica", "bold").setFontSize(9);
        doc.text("Note:", marginX, y);
        y += 5;
        doc.setFont("helvetica", "normal").setFontSize(8);
        const wrappedNote = doc.splitTextToSize(
          noteText,
          pageWidth - 2 * marginX
        );
        wrappedNote.forEach((ln) => {
          if (pageHeight - y < 10) {
            doc.addPage();
            y = marginY;
          }
          doc.text(ln, marginX, y);
          y += 4.5;
        });
        y += 4;
      }
    }

    // ---- CONDIZIONI DI PAGAMENTO ----
    const condizioni = [
      "1) Termini di pagamento: 40% anticipato all'ordine, saldo ad avviso merce pronta ns. deposito.",
      "2) Condizioni generali di vendita: L'autorizzazione al reso, o alla sostituzione, per quanto non conforme",
      "   all'ordine, deve essere preventivamente autorizzata dal produttore.",
      "3) Spese di deposito: Nel caso di mancato pagamento e/o ritardo nel ritiro e/o ingiustificato rifiuto della",
      "   merce da parte del cliente il venditore si riserva il diritto di addebitare al cliente i costi di deposito",
      "   della merce ex art. 1781 c.c. in misura di 30€ per ogni giorno di ritardo dalla data di presunta consegna.",
    ];
    const condLineH = 4.5;
    const condBlockH = 6 + condizioni.length * condLineH + 4;
    if (pageHeight - y < condBlockH) {
      doc.addPage();
      y = marginY;
    }
    doc.setTextColor(150).setFont("helvetica", "bold").setFontSize(9);
    doc.text("Condizioni di pagamento:", marginX, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(8);
    condizioni.forEach((ln) => {
      doc.text(ln, marginX, y);
      y += condLineH;
    });
    doc.setTextColor(0);
    y += 4;

    const filenameBase = `${isOrder ? "Ordine" : "Preventivo"}_N_${
      quote.id
    }.pdf`;
    const isDev =
      process.env.NODE_ENV === "development" ||
      import.meta?.env?.MODE === "development" ||
      process.env?.VUE_APP_NODE_ENV === "development";

    const withSuffixBeforeExt = (filename, suffix) => {
      if (!suffix) return filename;
      const dot = filename.lastIndexOf(".");
      if (dot <= 0) return `${filename}.${suffix}`;
      return `${filename.slice(0, dot)}.${suffix}${filename.slice(dot)}`;
    };

    // Override esplicito (es. PDF pagamento "tipologia_YYYY-MM-DD_HH-mm.pdf"):
    // usato così com'è, senza suffisso ".dev". Senza override resta il
    // comportamento esistente del documento principale.
    const finalName = filenameOverride
      ? filenameOverride
      : withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");
    const blob = doc.output("blob");
    const file = new File([blob], finalName, { type: "application/pdf" });

    try {
      await uploadQuoteDocument({
        quoteId: quote.id,
        document: file,
        filename: finalName,
      });
      toast.success("PDF generato e caricato con successo!");
      doc.save(finalName);
      // Solo tipo di documento ed esito: mai nome file, cliente o importi.
      trackEvent(EVENTS.QUOTE_EXPORTED, {
        action: "export",
        kind: isOrder ? "order" : "quote",
        feature: "pdf",
        outcome: "success",
      });
    } catch (error) {
      console.error("Errore durante l'upload del PDF:", error);
      trackEvent(EVENTS.QUOTE_EXPORTED, {
        action: "export",
        kind: isOrder ? "order" : "quote",
        feature: "pdf",
        outcome: "error",
      });
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Errore durante il caricamento del PDF."
      );
      return;
    }

    if (onComplete) await onComplete();
  } catch (error) {
    toast.error(
      error?.response?.data?.details ||
        error?.response?.data?.error ||
        "Errore durante la generazione/caricamento del PDF."
    );
  }
}
